import React, {useState, useRef, useEffect} from 'react';

export const DiceComponent = (props) => {
    const containerRef = useRef(null);
    const [position, setPosition] = useState({x: 0, y: 0});
    const [diceSent, setDiceSent] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [containerSize, setContainerSize] = useState({width: 0, height: 0});
    const [diceSize, setDiceSize] = useState({width: 50, height: 50});
    const lastPositionRef = useRef({x: 0, y: 0});
    const lastTimeRef = useRef(Date.now());
    const velocityRef = useRef({x: 0, y: 0});
    const [diceOffsets, setDiceOffsets] = useState([]);

    // Generate fixed offsets for additional dice
    useEffect(() => {
        if (!props.nbDices || props.nbDices <= 1) {
            setDiceOffsets([]);
            return;
        }

        const offsets = [];
        for (let i = 1; i < props.nbDices; i++) {
            // Random offset between -15 and 15 pixels for both x and y
            const offsetX = Math.random() * 30 - 15;
            const offsetY = Math.random() * 30 - 15;
            offsets.push({ x: offsetX, y: offsetY });
        }

        setDiceOffsets(offsets);
    }, [props.nbDices]);

    // Set up the container and center the dice initially
    useEffect(() => {
        if (containerRef.current) {
            const {width, height} = containerRef.current.getBoundingClientRect();
            setContainerSize({width, height});
            // Center the dice
            setPosition({
                x: (width - diceSize.width) / 2,
                y: (height - diceSize.height) / 2
            });
        }

        const handleResize = () => {
            if (containerRef.current) {
                const {width, height} = containerRef.current.getBoundingClientRect();
                setContainerSize({width, height});
                // Re-center the dice on resize
                setPosition({
                    x: (width - diceSize.width) / 2,
                    y: (height - diceSize.height) / 2
                });
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Calculate positions for all dice based on main dice position
    const getAllDicePositions = () => {
        if (!props.nbDices || props.nbDices <= 1) return [position];

        const positions = [position];

        diceOffsets.forEach(offset => {
            const newX = Math.max(0, Math.min(position.x + offset.x, containerSize.width - diceSize.width));
            const newY = Math.max(0, Math.min(position.y + offset.y, containerSize.height - diceSize.height));
            positions.push({ x: newX, y: newY });
        });

        return positions;
    };

    const calculateVelocity = (currentX, currentY) => {
        const now = Date.now();
        const deltaTime = now - lastTimeRef.current;

        if (deltaTime > 0) {
            const vx = (currentX - lastPositionRef.current.x) / deltaTime * 1000; // px per second
            const vy = (currentY - lastPositionRef.current.y) / deltaTime * 1000; // px per second

            velocityRef.current = {x: vx, y: vy};
        }

        lastPositionRef.current = {x: currentX, y: currentY};
        lastTimeRef.current = now;
    };

    const checkBoundariesAndVelocity = (x, y) => {
        const velocityThreshold = 300; // px per second
        const totalVelocity = Math.sqrt(
            velocityRef.current.x ** 2 + velocityRef.current.y ** 2
        );

        // Check boundaries
        const maxX = containerSize.width - diceSize.width;
        const maxY = containerSize.height - diceSize.height;

        let hitBoundary = false;
        let newX = x;
        let newY = y;

        if (x <= 0) {
            newX = 0;
            hitBoundary = true;
        } else if (x >= maxX) {
            newX = maxX;
            hitBoundary = true;
        }

        if (y <= 0) {
            newY = 0;
            hitBoundary = true;
        } else if (y >= maxY) {
            newY = maxY;
            hitBoundary = true;
        }

        // If we hit a boundary with enough velocity, send the dice
        if (hitBoundary && totalVelocity > velocityThreshold && props.callback && !diceSent) {
            const normV = Math.sqrt(velocityRef.current.x ** 2 + velocityRef.current.y ** 2);
            if (normV > 0) {
                velocityRef.current.x /= normV;
                velocityRef.current.y /= normV;
            }
            console.log("Sending dice with velocity", velocityRef.current);
            props.callback(2 * x / maxX - 1, 2 * y / maxY - 1, velocityRef.current.x, velocityRef.current.y);
            setDiceSent(true);
        }

        return {x: newX, y: newY};
    };

    const handleMouseDown = (e) => {
        setIsDragging(true);
        // Reset velocity tracking
        lastTimeRef.current = Date.now();
        lastPositionRef.current = {x: position.x, y: position.y};
        velocityRef.current = {x: 0, y: 0};
    };

    const handleMouseMove = (e) => {
        if (!isDragging) return;

        let clientX, clientY;

        // Handle both mouse and touch events
        if (e.touches) {
            clientX = e.touches[0].clientX;
            clientY = e.touches[0].clientY;
        } else {
            clientX = e.clientX;
            clientY = e.clientY;
        }

        const rect = containerRef.current.getBoundingClientRect();
        const x = clientX - rect.left - (diceSize.width / 2);
        const y = clientY - rect.top - (diceSize.height / 2);

        calculateVelocity(x, y);
        const newPosition = checkBoundariesAndVelocity(x, y);
        setPosition(newPosition);
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    return (
        <div
            ref={containerRef}
            style={{
                position: 'relative',
                width: '100%',
                height: '100%',
                border: '1px solid #ccc',
                overflow: 'hidden',
                touchAction: 'none',
                marginBottom: '32px'
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleMouseDown}
            onTouchMove={handleMouseMove}
            onTouchEnd={handleMouseUp}
        >
            {getAllDicePositions().map((pos, index) => (
                <img
                    key={index}
                    src="/public/generated/dice_asset.webp"
                    alt={`Dice ${index + 1}`}
                    style={{
                        position: 'absolute',
                        width: `${diceSize.width}px`,
                        height: `${diceSize.height}px`,
                        left: `${pos.x}px`,
                        top: `${pos.y}px`,
                        cursor: isDragging ? 'grabbing' : 'grab',
                        userSelect: 'none',
                        WebkitUserSelect: 'none',
                        touchAction: 'none',
                        zIndex: index === 0 ? 2 : 1 // Main dice has higher z-index
                    }}
                    draggable={false}
                />
            ))}
        </div>
    );
};
